import Dexie from 'dexie';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import Fuse from 'fuse.js';
import FuseIndex = Fuse.FuseIndex;

import ThriftSerializer from '../core/libs/ThriftSerializer';

import { TAppContact } from 'TProtocol/common/models';
import ContactHelper from '../core/libs/ContactHelper';
import { TAppContactWithId } from '../core/libs/utils';
import { IUSearchItem } from '../cache/Search';

class ContactsDexieDatabase extends Dexie {
  eContacts!: Dexie.Table<IUDBEncryptedContact, string>;
  images: Dexie.Table<IUDBImage, string>;
  meta!: Dexie.Table<IUKeyValue, string>;

  constructor() {
    super('allContacts');
    this.version(7).stores({
      eContacts: '&contactId, fullName',
      images: '&src',
      meta: 'key'
    });
  }
}

export interface IUDBKeyedContact {
  contactId: string;
  fullName: string;
  archived?: boolean;
}

interface IUDBEncryptedContact extends IUDBKeyedContact {
  encryptedContact: string;
}

export const appContactToKeyed = (appContact: TAppContactWithId): IUDBKeyedContact => {
  return {
    contactId: appContact.contactId,
    fullName: ContactHelper.getName(appContact),
    archived: appContact.archived
  };
};

export interface IUDBImage {
  src: string;
  url: string;
}

export interface IUKeyValue {
  key: string;
  value: string;
}

export interface IUFuseCache {
  contacts: IUSearchItem[],
  index: FuseIndex<IUSearchItem>,
}

export class ContactsDatabase {
  db: ContactsDexieDatabase;
  userId: string;

  constructor() {
    this.db = new ContactsDexieDatabase();
  }

  /* Global */

  setUserId(userId: string) {
    this.userId = userId;
  }

  open() {
    return this.db.open();
  }

  close() {
    return this.db.close();
  }

  delete() {
    return this.db.delete();
  }

  /* Contacts */
  async getContacts() {
    const encryptedContacts = await this.db.eContacts.toArray();

    return encryptedContacts.map((eContact: IUDBEncryptedContact) => {
      return {
        contactId: eContact.contactId,
        fullName: eContact.fullName,
        archived: eContact.archived
      };
    });
  }

  async getContact(contactId?: string): Promise<TAppContact | undefined> {
    if (contactId === undefined) {
      return undefined;
    }

    const eContact = await this.db.eContacts.get(contactId);
    if (eContact === undefined) {
      return undefined;
    }

    return ThriftSerializer.deserialize(
      Buffer.from(AES.decrypt(eContact.encryptedContact, this.userId).toString(Utf8))
    );
  }

  async updateContacts(contacts: TAppContact[], reset = false, deletedContactIds: string[] = []) {
    const encryptedContacts: IUDBEncryptedContact[] = [];

    for (let i = 0; i < contacts.length; ++i) {
      const contactId = contacts[i].contactId;
      if (contactId !== undefined && !deletedContactIds.includes(contactId)) {
        const serializedContact = ThriftSerializer.serialize(contacts[i]);
        const encryptedContact = AES.encrypt(serializedContact.toString(), this.userId).toString();
        const fullName = ContactHelper.getName(contacts[i]);
        encryptedContacts.push({ contactId, fullName, encryptedContact, archived: contacts[i].archived });
      }
    }

    if (deletedContactIds.length > 0) {
      await this.deleteContacts(deletedContactIds);
    }

    if (reset) {
      await this.clearContacts();
    }

    try {
      await this.db.eContacts.bulkPut(encryptedContacts);
    } catch (e) {
      return;
    }
  }

  async deleteContacts(contactIds: string[]) {
    await this.db.eContacts.bulkDelete(contactIds);
  }

  async clearContacts() {
    await this.db.eContacts.clear();
  }

  /* Images */

  async getImage(src: string) {
    return this.db.images.get(src);
  }

  async putImage(src: string, url: string) {
    await this.db.images.put({ src, url }, src);
  }

  /* Meta */

  private async getKeyValue(key: string) {
    const modIds = await this.db.meta.filter((m) => {
      return m['key'] === key;
    }).toArray();

    return modIds[0]?.value;
  }

  async putModId(modId: string) {
    await this.db.meta.put({ key: 'latestModId', value: modId });
  }

  async getModId() {
    return this.getKeyValue('latestModId');
  }

  async setFuseCache(cache: IUFuseCache) {
    await this.db.meta.put({
      key: 'fuseContacts',
      value: AES.encrypt(JSON.stringify(cache.contacts), this.userId).toString()
    });
    await this.db.meta.put({
      key: 'fuseIndex',
      value: AES.encrypt(JSON.stringify(cache.index.toJSON()), this.userId).toString()
    });
  }

  async getFuseCache(): Promise<IUFuseCache | undefined> {
    const contacts = await this.getKeyValue('fuseContacts');
    const index = await this.getKeyValue('fuseIndex');
    if (contacts !== undefined && index !== undefined) {
      return {
        contacts: JSON.parse(AES.decrypt(contacts, this.userId).toString(Utf8)),
        index: Fuse.parseIndex(JSON.parse(AES.decrypt(index, this.userId).toString(Utf8)))
      };
    }
    return undefined;
  }

}
